import { computed, ref } from "vue";
import { storeToRefs } from "pinia";

import * as tmi from "@/constants";

import { useAuthStore } from "@/stores/Auth";
import { useProductsStore } from "@/stores/Products";

import type { Product } from "@tmi/types/entities";

import { isFreelyAvailableContent } from "@/utils/access-utils";

/*********************************************************************/
//                       NON-REACTIVE CONSTANTS                      //
/*********************************************************************/
import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

// A list of product ids for content that is associated with the products in the collection (inclusive)
const includeProductFilter = ref< Product[] >( [] );
const productIdFilter = computed< string[] >( () => includeProductFilter.value.map( ( filter: Product ) => filter.id ) );

export function useProductFilter() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const productsStore = useProductsStore();

    const hasProductsToFilter = computed< boolean >( () => isAuthenticated.value && productsStore.displayableProducts.length > 0 );

    function filterOutSubscriptionBasedProducts( products: Product[] ): boolean {
        logger.debug( '[ product-filter ].filterOutSubscriptionBasedProducts -> slug === ', tmi.TmiPublicContentPermissionKey );
        return products.some( ( product: Product ): boolean => isFreelyAvailableContent( product.slug ) );
    }

    function filterOutItemsThatDoNotContainSelectedProducts( products: Product[] ): boolean {
        if ( productIdFilter.value?.length === 0 ) {
            return true;
        }

        return products.some( ( product: Product ) => productIdFilter.value.includes( product.id ) );
    }

    function resetSelectedProducts(): void {
        includeProductFilter.value = [];
    }

    function setSelectedProduct( productSlug: string ): void {
        // Initially, clear the product filter
        resetSelectedProducts();

        if ( !! productSlug ) {
            const activeProduct: Product | undefined = useProductsStore().productBySlug( `${ productSlug }` );
            // If this page is loaded within the context of a product, set the filter for the product, o/w leave the filter cleared
            if ( activeProduct ) {
                includeProductFilter.value.push( activeProduct );
            }
        }
    }

    return {
        filterOutItemsThatDoNotContainSelectedProducts,
        filterOutSubscriptionBasedProducts,
        hasProductsToFilter,
        includeProductFilter,
        setSelectedProduct,
        resetSelectedProducts
    }
}

export type ProductFilter = ReturnType< typeof useProductFilter >;
